import React, { useState } from 'react'
import {
  Badge,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Typography,
  Tooltip,
  Divider,
  Alert,
  Modal,
  Button,
  Select
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBriefcase,
  faBusinessTime,
  faIdCard,
  faRegistered,
  faHashtag,
  faBuilding,
  faGears,
  faInfoCircle,
  faCheckCircle,
  faExclamationTriangle,
  faCertificate,
  faClipboardCheck,
  faArrowDown,
  faEdit,
  faTrash,
  faPlus,
  faSave,
  faTimes,
  faGripVertical
} from '@fortawesome/free-solid-svg-icons'
import { hexToRGBA, specialFieldValidation } from '../../../../lib/utils'
import {
  CLINIC_FORM_CATEGORY_ICONS,
  CLINIC_FORM_CATEGORY_COLORS,
  CLINIC_FORM_CATEGORY_LABELS
} from '../../config/form-categories'
import Templates from '../../utils/templates'
import { clinicTabsEnums } from '../../config/clinic-tabs-enums'

const { Title, Text, Paragraph } = Typography

// WorkflowStepCard Component
const WorkflowStepCard = ({
  item,
  index,
  type,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onRemove,
  onDragStart,
  onDragOver,
  onDrop,
  getProcessStepOptions,
  token
}) => {
  const [editLabel, setEditLabel] = useState(item.label)
  const [editType, setEditType] = useState(item.key)

  const handleSave = () => {
    onSave(index, editLabel, editType)
  }

  const handleCancel = () => {
    setEditLabel(item.label)
    setEditType(item.key)
    onCancel()
  }

  const stepTypeColor =
    type === 'completed' ? token.colorSuccess : type === 'cancelled' ? token.colorError : token.colorPrimary

  const isCompletedOrCancelled = type === 'completed' || type === 'cancelled'

  return (
    <div
      draggable={!isEditing && !isCompletedOrCancelled}
      onDragStart={() => !isEditing && !isCompletedOrCancelled && onDragStart()}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: hexToRGBA(stepTypeColor, 0.04),
        border: `1px solid ${hexToRGBA(stepTypeColor, 0.2)}`,
        borderRadius: '6px',
        padding: '8px 12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        transition: 'all 0.2s ease',
        cursor: isEditing ? 'default' : isCompletedOrCancelled ? 'default' : 'grab',
        width: '100%',
        minHeight: '44px'
      }}
    >
      {/* Left side - drag handle and step number */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {!isCompletedOrCancelled && (
          <FontAwesomeIcon icon={faGripVertical} style={{ color: token.colorTextSecondary, fontSize: '12px' }} />
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '22px',
            height: '22px',
            backgroundColor: stepTypeColor,
            color: 'white',
            borderRadius: '50%',
            fontSize: '12px',
            fontWeight: '600',
            flexShrink: 0
          }}
        >
          {index + 1}
        </div>
      </div>

      {/* Center - Content */}
      <div style={{ flex: 1, marginLeft: '12px', marginRight: '12px' }}>
        {isEditing ? (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <Select
                value={editType}
                onChange={setEditType}
                style={{ width: '100%' }}
                size='small'
                options={getProcessStepOptions()}
                placeholder='Type'
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                size='small'
                placeholder='Label'
              />
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Text style={{ fontSize: '14px', fontWeight: '600', color: token.colorTextHeading, lineHeight: '1.2' }}>
              {item.label}
            </Text>
            <Text style={{ fontSize: '12px', color: token.colorTextSecondary }}>
              {getProcessStepOptions().find((opt) => opt.value === item.key)?.label || item.key}
            </Text>
          </div>
        )}
      </div>

      {/* Right side - action buttons */}
      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
        {isEditing ? (
          <>
            <Button
              size='small'
              type='primary'
              icon={<FontAwesomeIcon icon={faSave} />}
              onClick={handleSave}
              style={{ padding: '4px 8px', height: '28px' }}
            />
            <Button
              size='small'
              icon={<FontAwesomeIcon icon={faTimes} />}
              onClick={handleCancel}
              style={{ padding: '4px 8px', height: '28px' }}
            />
          </>
        ) : (
          <>
            <Button
              size='small'
              icon={<FontAwesomeIcon icon={faEdit} />}
              onClick={onEdit}
              style={{ padding: '4px 8px', height: '28px' }}
            />
            <Button
              size='small'
              danger
              icon={<FontAwesomeIcon icon={faTrash} />}
              onClick={onRemove}
              style={{ padding: '4px 8px', height: '28px' }}
            />
          </>
        )}
      </div>
    </div>
  )
}

// NewStepForm Component
const NewStepForm = ({ onSave, onCancel, getProcessStepOptions, token }) => {
  const [newLabel, setNewLabel] = useState('')
  const [newType, setNewType] = useState('')

  const handleSave = () => {
    if (newLabel.trim() && newType) {
      onSave(newLabel.trim(), newType)
      setNewLabel('')
      setNewType('')
    }
  }

  const handleCancel = () => {
    setNewLabel('')
    setNewType('')
    onCancel()
  }

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '450px',
        marginTop: '8px'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: hexToRGBA(token.colorPrimary, 0.04),
          border: `1px dashed ${hexToRGBA(token.colorPrimary, 0.3)}`,
          borderRadius: '6px',
          padding: '8px 12px',
          minHeight: '44px'
        }}
      >
        {/* Left side - icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FontAwesomeIcon icon={faPlus} style={{ color: token.colorPrimary, fontSize: '14px' }} />
          <Text strong style={{ color: token.colorPrimary, fontSize: '14px' }}>
            New Step
          </Text>
        </div>

        {/* Center - inputs */}
        <div
          style={{
            flex: 1,
            marginLeft: '12px',
            marginRight: '12px',
            display: 'flex',
            gap: '8px',
            alignItems: 'center'
          }}
        >
          <div style={{ flex: 1 }}>
            <Select
              value={newType}
              onChange={setNewType}
              placeholder='Select type'
              style={{ width: '100%' }}
              size='small'
              options={getProcessStepOptions()}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder='Enter label'
              size='small'
            />
          </div>
        </div>

        {/* Right side - action buttons */}
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
          <Button
            size='small'
            type='primary'
            icon={<FontAwesomeIcon icon={faSave} />}
            onClick={handleSave}
            disabled={!newLabel.trim() || !newType}
            style={{ padding: '4px 8px', height: '28px' }}
          >
            Add
          </Button>
          <Button
            size='small'
            icon={<FontAwesomeIcon icon={faTimes} />}
            onClick={handleCancel}
            style={{ padding: '4px 8px', height: '28px' }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

export const createPracticeDetailsTab = ({
  token,
  unfilledFields,
  practiceDetailsForm,
  specialFormState,
  setSpecialFormState,
  bpmProfile,
  recordData,
  handleFormSubmit,
  handleValuesChange,
  locationDataValid,
  basicInfoValid,
  bpmConfig,
  setBpmConfig,
  editingStepIndex,
  setEditingStepIndex,
  addingStep,
  setAddingStep,
  draggedItem,
  setDraggedItem
}) => {
  // Helper functions for workflow management
  const handleEditStep = (index) => {
    setEditingStepIndex(index)
  }

  const handleSaveStep = (index, newLabel, newType) => {
    const updatedConfig = [...bpmConfig]
    updatedConfig[index] = { ...updatedConfig[index], label: newLabel, key: newType }
    const finalConfig = ensureCompletedCancelledAtEnd(updatedConfig)
    setBpmConfig(finalConfig)
    setEditingStepIndex(null)

    practiceDetailsForm.setFieldsValue({
      ...practiceDetailsForm.getFieldsValue(),
      bpmConfig: finalConfig
    })
    handleValuesChange({
      ...practiceDetailsForm.getFieldsValue(),
      bpmConfig: finalConfig
    })
  }

  const handleCancelEdit = () => {
    setEditingStepIndex(null)
  }

  const handleRemoveStep = (index) => {
    const updatedConfig = bpmConfig.filter((_, i) => i !== index)
    setBpmConfig(updatedConfig)
    practiceDetailsForm.setFieldsValue({
      ...practiceDetailsForm.getFieldsValue(),
      bpmConfig: updatedConfig
    })
    handleValuesChange({
      ...practiceDetailsForm.getFieldsValue(),
      bpmConfig: updatedConfig
    })
  }

  const handleAddStep = () => {
    setAddingStep(true)
  }

  const ensureCompletedCancelledAtEnd = (config) => {
    const completedStep = config.find((step) => step.key === 'completed')
    const cancelledStep = config.find((step) => step.key === 'cancelled')
    const otherSteps = config.filter((step) => step.key !== 'completed' && step.key !== 'cancelled')

    const result = [...otherSteps]
    if (completedStep) result.push(completedStep)
    if (cancelledStep) result.push(cancelledStep)

    return result
  }

  const handleSaveNewStep = (label, type) => {
    const newStep = { label, key: type }
    const updatedConfig = ensureCompletedCancelledAtEnd([...bpmConfig, newStep])
    setBpmConfig(updatedConfig)
    setAddingStep(false)
    practiceDetailsForm.setFieldsValue({
      ...practiceDetailsForm.getFieldsValue(),
      bpmConfig: updatedConfig
    })
    handleValuesChange({
      ...practiceDetailsForm.getFieldsValue(),
      bpmConfig: updatedConfig
    })
  }

  const handleCancelAdd = () => {
    setAddingStep(false)
  }

  const handleDragStart = (index) => {
    setDraggedItem(index)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    if (draggedItem === null) return

    const updatedConfig = [...bpmConfig]
    const draggedStep = updatedConfig[draggedItem]

    // Don't allow moving completed/cancelled steps
    if (draggedStep.key === 'completed' || draggedStep.key === 'cancelled') {
      setDraggedItem(null)
      return
    }

    // Don't allow dropping onto completed/cancelled positions
    const dropStep = updatedConfig[dropIndex]
    if (dropStep && (dropStep.key === 'completed' || dropStep.key === 'cancelled')) {
      setDraggedItem(null)
      return
    }

    updatedConfig.splice(draggedItem, 1)
    updatedConfig.splice(dropIndex, 0, draggedStep)

    setBpmConfig(updatedConfig)
    setDraggedItem(null)
    practiceDetailsForm.setFieldsValue({
      ...practiceDetailsForm.getFieldsValue(),
      bpmConfig: updatedConfig
    })
    handleValuesChange({
      ...practiceDetailsForm.getFieldsValue(),
      bpmConfig: updatedConfig
    })
  }

  const getProcessStepOptions = () => {
    if (!bpmProfile?.data?.processSteps) return []
    return bpmProfile.data.processSteps
      .filter((step) => step.key !== 'creation')
      .map((step) => ({ value: step.key, label: step.name }))
  }

  const renderWorkflowStep = (item, index) => {
    const isCompleted = item.key === 'completed'
    const isCancelled = item.key === 'cancelled'

    // For completed/cancelled steps, show them side by side
    if (isCompleted) {
      const cancelledIndex = bpmConfig.findIndex((step) => step.key === 'cancelled')
      const cancelledStep = cancelledIndex !== -1 ? bpmConfig[cancelledIndex] : null

      return (
        <div
          key={`completed-cancelled-${index}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              width: '100%',
              maxWidth: '100%'
            }}
          >
            {/* Completed Step */}
            <WorkflowStepCard
              item={item}
              index={index}
              type='completed'
              isEditing={editingStepIndex === index}
              onEdit={() => handleEditStep(index)}
              onSave={handleSaveStep}
              onCancel={handleCancelEdit}
              onRemove={() => handleRemoveStep(index)}
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              getProcessStepOptions={getProcessStepOptions}
              token={token}
            />

            {/* Cancelled Step */}
            {cancelledStep && (
              <WorkflowStepCard
                item={cancelledStep}
                index={cancelledIndex}
                type='cancelled'
                isEditing={editingStepIndex === cancelledIndex}
                onEdit={() => handleEditStep(cancelledIndex)}
                onSave={handleSaveStep}
                onCancel={handleCancelEdit}
                onRemove={() => handleRemoveStep(cancelledIndex)}
                onDragStart={() => handleDragStart(cancelledIndex)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, cancelledIndex)}
                getProcessStepOptions={getProcessStepOptions}
                token={token}
              />
            )}
          </div>

          {/* Arrow after the completed/cancelled pair */}
          {index < bpmConfig.length - 1 && !isCancelled && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '40px',
                position: 'relative'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <div
                  style={{
                    width: '2px',
                    height: '12px',
                    backgroundColor: hexToRGBA(token.colorPrimary, 0.3)
                  }}
                />
                <FontAwesomeIcon
                  icon={faArrowDown}
                  style={{
                    color: token.colorPrimary,
                    fontSize: '16px'
                  }}
                />
                <div
                  style={{
                    width: '2px',
                    height: '12px',
                    backgroundColor: hexToRGBA(token.colorPrimary, 0.3)
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )
    }

    // Skip cancelled if it's already rendered with completed
    if (isCancelled && bpmConfig.findIndex((step) => step.key === 'completed') < index) {
      return null
    }

    // Regular step
    return (
      <div
        key={index}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <WorkflowStepCard
          item={item}
          index={index}
          isEditing={editingStepIndex === index}
          onEdit={() => handleEditStep(index)}
          onSave={handleSaveStep}
          onCancel={handleCancelEdit}
          onRemove={() => handleRemoveStep(index)}
          onDragStart={() => handleDragStart(index)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, index)}
          getProcessStepOptions={getProcessStepOptions}
          token={token}
        />

        {/* Arrow Connector */}
        {index < bpmConfig.length - 1 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '40px',
              position: 'relative'
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <div
                style={{
                  width: '2px',
                  height: '12px',
                  backgroundColor: hexToRGBA(token.colorPrimary, 0.3)
                }}
              />
              <FontAwesomeIcon
                icon={faArrowDown}
                style={{
                  color: token.colorPrimary,
                  fontSize: '16px'
                }}
              />
              <div
                style={{
                  width: '2px',
                  height: '12px',
                  backgroundColor: hexToRGBA(token.colorPrimary, 0.3)
                }}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  return {
    key: clinicTabsEnums.PRACTICE_DETAILS,

    disabled: !locationDataValid || !basicInfoValid,
    label: (
      <span>
        <FontAwesomeIcon
          icon={CLINIC_FORM_CATEGORY_ICONS.PRACTICE_DETAILS}
          style={{ marginRight: '8px', color: CLINIC_FORM_CATEGORY_COLORS.PRACTICE_DETAILS }}
        />
        {CLINIC_FORM_CATEGORY_LABELS.PRACTICE_DETAILS}
        <Badge count={unfilledFields[3]} style={{ marginLeft: '8px', backgroundColor: token.colorError }} />
      </span>
    ),
    children: (
      <Form
        onFinish={handleFormSubmit}
        onFinishFailed={(errorInfo) => {
          Modal.error({
            title: 'Form Validation Error',
            content: errorInfo.errorFields[0].errors[0]
          })
        }}
        name='form'
        form={practiceDetailsForm}
        initialValues={{
          ...Templates.dataTemplate(),
          ...recordData
        }}
        layout='vertical'
        onValuesChange={handleValuesChange}
      >
        <div style={{ padding: '24px 0', background: hexToRGBA(token.colorBgLayout, 0.3) }}>
          <Row gutter={[32, 32]}>
            {/* Practice Details Column */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space size={12}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        backgroundColor: hexToRGBA(token.colorPrimary, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faBriefcase} style={{ color: token.colorPrimary, fontSize: '18px' }} />
                    </div>
                    <div>
                      <Text strong style={{ fontSize: '18px', display: 'block' }}>
                        Practice Information
                      </Text>
                      <Text type='secondary' style={{ fontSize: '14px' }}>
                        Legal practice identification details
                      </Text>
                    </div>
                  </Space>
                }
                bordered={false}
                style={{
                  borderRadius: '16px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f0ff 100%)',
                  border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`,
                  height: '100%'
                }}
                bodyStyle={{ padding: '32px' }}
                headStyle={{
                  borderBottom: 'none',
                  backgroundColor: 'transparent',
                  padding: '24px 32px 0 32px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                  <div>
                    <Title level={5} style={{ margin: '0 0 20px 0', color: token.colorPrimary }}>
                      <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '8px' }} />
                      Medical Practice Details
                    </Title>

                    <Paragraph style={{ marginBottom: '24px', color: token.colorTextSecondary }}>
                      Enter the official practice information as registered with medical authorities. This information
                      is used for billing, compliance, and professional verification.
                    </Paragraph>

                    {/* Practice Name */}
                    <Form.Item
                      name={Templates.dataModel.practiceName.key}
                      label={
                        <Space>
                          <Text strong style={{ fontSize: '15px' }}>
                            {Templates.dataModel.practiceName.label}
                          </Text>
                          <Tooltip title='The official name of the medical practice as registered'>
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              style={{ color: token.colorPrimary, fontSize: '12px' }}
                            />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: Templates.dataModel.practiceName.required,
                          message: Templates.dataModel.practiceName.validationMsg
                        }
                      ]}
                      style={{ marginBottom: '24px' }}
                    >
                      <Input
                        size='large'
                        onChange={(e) => {
                          specialFieldValidation(practiceDetailsForm, specialFormState, setSpecialFormState, {
                            key: 'practiceName',
                            event: e,
                            validationConfig: {
                              letters: { allowed: true, onlyCaps: false },
                              numbers: true,
                              spaces: true,
                              specialChars: true
                            }
                          })
                        }}
                        maxLength={50}
                        placeholder={Templates.dataModel.practiceName.placeholder}
                        prefix={
                          <FontAwesomeIcon icon={faIdCard} style={{ color: token.colorPrimary, marginRight: '8px' }} />
                        }
                        style={{
                          borderRadius: '8px',
                          fontSize: '16px',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>

                    {/* Practice Registration Number */}
                    <Form.Item
                      name={Templates.dataModel.practiceRegNumber.key}
                      label={
                        <Space>
                          <Text strong style={{ fontSize: '15px' }}>
                            {Templates.dataModel.practiceRegNumber.label}
                          </Text>
                          <Tooltip title='The official registration number issued to the practice by medical authorities'>
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              style={{ color: token.colorPrimary, fontSize: '12px' }}
                            />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: Templates.dataModel.practiceRegNumber.required,
                          message: Templates.dataModel.practiceRegNumber.validationMsg
                        }
                      ]}
                      style={{ marginBottom: '24px' }}
                    >
                      <Input
                        size='large'
                        onChange={(e) => {
                          specialFieldValidation(practiceDetailsForm, specialFormState, setSpecialFormState, {
                            key: 'practiceRegNumber',
                            event: e,
                            validationConfig: {
                              letters: { allowed: true, onlyCaps: false },
                              numbers: true,
                              spaces: true,
                              specialChars: true
                            }
                          })
                        }}
                        maxLength={50}
                        placeholder={Templates.dataModel.practiceRegNumber.placeholder}
                        prefix={
                          <FontAwesomeIcon
                            icon={faRegistered}
                            style={{ color: token.colorPrimary, marginRight: '8px' }}
                          />
                        }
                        style={{
                          borderRadius: '8px',
                          fontSize: '16px',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>

                    {/* Practice Number */}
                    <Form.Item
                      name={Templates.dataModel.practiceNumber.key}
                      label={
                        <Space>
                          <Text strong style={{ fontSize: '15px' }}>
                            {Templates.dataModel.practiceNumber.label}
                          </Text>
                          <Tooltip title='The unique identifier assigned to this practice for billing and identification'>
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              style={{ color: token.colorPrimary, fontSize: '12px' }}
                            />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: Templates.dataModel.practiceNumber.required,
                          message: Templates.dataModel.practiceNumber.validationMsg
                        }
                      ]}
                    >
                      <Input
                        size='large'
                        onChange={(e) => {
                          specialFieldValidation(practiceDetailsForm, specialFormState, setSpecialFormState, {
                            key: 'practiceNumber',
                            event: e,
                            validationConfig: {
                              letters: { allowed: true, onlyCaps: false },
                              numbers: true,
                              spaces: false,
                              specialChars: true
                            }
                          })
                        }}
                        maxLength={50}
                        placeholder={Templates.dataModel.practiceNumber.placeholder}
                        prefix={
                          <FontAwesomeIcon icon={faHashtag} style={{ color: token.colorPrimary, marginRight: '8px' }} />
                        }
                        style={{
                          borderRadius: '8px',
                          fontSize: '16px',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>
                  </div>

                  <Divider style={{ margin: '8px 0', borderColor: hexToRGBA(token.colorBorder, 0.3) }} />

                  {/* Compliance Card */}
                  <Card
                    size='small'
                    style={{
                      borderRadius: '12px',
                      border: `2px solid ${hexToRGBA(token.colorSuccess, 0.2)}`,
                      backgroundColor: hexToRGBA(token.colorSuccess, 0.05),
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <div style={{ marginBottom: '12px' }}>
                      <Space>
                        <FontAwesomeIcon icon={faCertificate} style={{ color: token.colorSuccess, fontSize: '16px' }} />
                        <Text strong style={{ color: token.colorSuccess, fontSize: '16px' }}>
                          Compliance & Verification
                        </Text>
                      </Space>
                    </div>

                    <div style={{ paddingLeft: '8px' }}>
                      <ul style={{ paddingLeft: '16px', margin: '0' }}>
                        <li>
                          <Text type='secondary' style={{ fontSize: '13px' }}>
                            Ensure all practice details match official registration documents
                          </Text>
                        </li>
                        <li>
                          <Text type='secondary' style={{ fontSize: '13px' }}>
                            Keep registration numbers up to date with licensing bodies
                          </Text>
                        </li>
                        <li>
                          <Text type='secondary' style={{ fontSize: '13px' }}>
                            Verify information is consistent across all platforms
                          </Text>
                        </li>
                        <li>
                          <Text type='secondary' style={{ fontSize: '13px' }}>
                            Maintain compliance with local healthcare regulations
                          </Text>
                        </li>
                      </ul>
                    </div>
                  </Card>

                  {/* Professional Setup Card */}
                  <Card
                    size='small'
                    style={{
                      borderRadius: '12px',
                      border: `2px solid ${hexToRGBA(token.colorPrimary, 0.2)}`,
                      backgroundColor: hexToRGBA(token.colorPrimary, 0.05),
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <div style={{ marginBottom: '12px' }}>
                      <Space>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: token.colorPrimary, fontSize: '16px' }} />
                        <Text strong style={{ color: token.colorPrimary, fontSize: '16px' }}>
                          Professional Setup
                        </Text>
                      </Space>
                    </div>

                    <div style={{ paddingLeft: '8px' }}>
                      <Text type='secondary' style={{ fontSize: '13px' }}>
                        Your practice details will be verified and integrated with healthcare systems to ensure
                        compliance and seamless operations.
                      </Text>
                    </div>
                  </Card>
                </div>
              </Card>
            </Col>

            {/* Business Process Column */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space size={12}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        backgroundColor: hexToRGBA(token.colorInfo, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faBusinessTime} style={{ color: token.colorInfo, fontSize: '18px' }} />
                    </div>
                    <div>
                      <Text strong style={{ fontSize: '18px', display: 'block' }}>
                        Business Process Configuration
                      </Text>
                      <Text type='secondary' style={{ fontSize: '14px' }}>
                        Define operational workflows and procedures
                      </Text>
                    </div>
                  </Space>
                }
                bordered={false}
                style={{
                  borderRadius: '16px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                  border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`,
                  height: '100%'
                }}
                bodyStyle={{ padding: '32px' }}
                headStyle={{
                  borderBottom: 'none',
                  backgroundColor: 'transparent',
                  padding: '24px 32px 0 32px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                  <div>
                    <Title level={5} style={{ margin: '0 0 20px 0', color: token.colorInfo }}>
                      <FontAwesomeIcon icon={faGears} style={{ marginRight: '8px' }} />
                      Workflow Configuration
                    </Title>

                    <Paragraph style={{ marginBottom: '24px', color: token.colorTextSecondary }}>
                      Configure the business process that defines how this facility will operate. This controls
                      appointment scheduling, billing workflows, patient management, and staff task assignments.
                    </Paragraph>

                    <Paragraph style={{ marginBottom: '24px', color: token.colorTextSecondary }}>
                      By default, we have a standard workflow that is used in facilities, this can be modified. You can
                      create new workflow steps, remove steps, and reorder steps.
                    </Paragraph>

                    <Form.Item name={Templates.dataModel.bpmConfig.key} hidden>
                      <Select options={[]} />
                    </Form.Item>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0',
                        width: '100%'
                      }}
                    >
                      {bpmConfig.map((item, index) => renderWorkflowStep(item, index))}

                      {/* Add New Step */}
                      {addingStep ? (
                        <NewStepForm
                          onSave={handleSaveNewStep}
                          onCancel={handleCancelAdd}
                          getProcessStepOptions={getProcessStepOptions}
                          token={token}
                        />
                      ) : (
                        <div style={{ marginTop: '8px' }}>
                          <Button
                            type='dashed'
                            icon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={handleAddStep}
                            style={{
                              width: '100%',
                              maxWidth: '500px',
                              height: '36px',
                              borderStyle: 'dashed',
                              borderColor: token.colorPrimary,
                              color: token.colorPrimary,
                              fontSize: '13px',
                              padding: '0 16px'
                            }}
                          >
                            Add Workflow Step
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  <Divider style={{ margin: '8px 0', borderColor: hexToRGBA(token.colorBorder, 0.3) }} />

                  {/* Business Process Workflow Explanation */}
                  <Card
                    size='small'
                    style={{
                      borderRadius: '12px',
                      border: `2px solid ${hexToRGBA(token.colorInfo, 0.2)}`,
                      backgroundColor: hexToRGBA(token.colorInfo, 0.05),
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      <Space>
                        <FontAwesomeIcon icon={faClipboardCheck} style={{ color: token.colorInfo, fontSize: '16px' }} />
                        <Text strong style={{ color: token.colorInfo, fontSize: '16px' }}>
                          Business Process Workflow Options
                        </Text>
                      </Space>
                    </div>

                    <div style={{ paddingLeft: '8px' }}>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong style={{ fontSize: '14px', color: token.colorTextHeading }}>
                          Standard Workflow (5 Phases):
                        </Text>
                        <Paragraph
                          style={{ margin: '8px 0 12px 0', fontSize: '13px', color: token.colorTextSecondary }}
                        >
                          The complete patient journey from booking to completion of visit.
                        </Paragraph>
                      </div>

                      <div style={{ paddingLeft: '12px', marginBottom: '20px' }}>
                        <div style={{ marginBottom: '12px' }}>
                          <Text strong style={{ fontSize: '13px', color: token.colorPrimary }}>
                            1. Appointment
                          </Text>
                          <Text style={{ fontSize: '13px', marginLeft: '8px' }}>
                            → Patients schedule visits in advance through booking system
                          </Text>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <Text strong style={{ fontSize: '13px', color: token.colorPrimary }}>
                            2. Check-In
                          </Text>
                          <Text style={{ fontSize: '13px', marginLeft: '8px' }}>
                            → Patients verify arrival using mobile OTP and provide visit details (symptoms, reason for
                            visit)
                          </Text>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <Text strong style={{ fontSize: '13px', color: token.colorPrimary }}>
                            3. Screening
                          </Text>
                          <Text style={{ fontSize: '13px', marginLeft: '8px' }}>
                            → Nursing staff conduct initial vital signs assessment (blood pressure, temperature, weight)
                          </Text>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <Text strong style={{ fontSize: '13px', color: token.colorPrimary }}>
                            4. Diagnosis
                          </Text>
                          <Text style={{ fontSize: '13px', marginLeft: '8px' }}>
                            → Healthcare professional consultation, examination, and treatment planning
                          </Text>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <Text strong style={{ fontSize: '13px', color: token.colorPrimary }}>
                            5. Reception
                          </Text>
                          <Text style={{ fontSize: '13px', marginLeft: '8px' }}>
                            → Administrative completion including payment processing and invoice generation
                          </Text>
                        </div>
                      </div>

                      <Divider style={{ margin: '16px 0', borderColor: hexToRGBA(token.colorBorder, 0.3) }} />

                      <div style={{ marginBottom: '12px' }}>
                        <Text strong style={{ fontSize: '14px', color: token.colorTextHeading }}>
                          Workflow Variations:
                        </Text>
                        <Paragraph
                          style={{ margin: '8px 0 12px 0', fontSize: '13px', color: token.colorTextSecondary }}
                        >
                          Customize the workflow to match your facility's operational model.
                        </Paragraph>
                      </div>

                      <ul style={{ paddingLeft: '16px', margin: '0' }}>
                        <li style={{ marginBottom: '8px' }}>
                          <Text strong style={{ fontSize: '13px' }}>
                            No Appointment:
                          </Text>
                          <Text style={{ fontSize: '13px', marginLeft: '8px' }}>
                            Walk-in facility without scheduled bookings (urgent care, emergency services)
                          </Text>
                        </li>
                        <li style={{ marginBottom: '8px' }}>
                          <Text strong style={{ fontSize: '13px' }}>
                            No Screening:
                          </Text>
                          <Text style={{ fontSize: '13px', marginLeft: '8px' }}>
                            Direct patient-to-provider workflow (specialist consultations, follow-up visits)
                          </Text>
                        </li>
                        <li style={{ marginBottom: '0' }}>
                          <Text strong style={{ fontSize: '13px' }}>
                            No Diagnosis:
                          </Text>
                          <Text style={{ fontSize: '13px', marginLeft: '8px' }}>
                            Service-based visits without clinical consultation (vaccinations, health screenings, lab
                            work)
                          </Text>
                        </li>
                      </ul>
                    </div>
                  </Card>

                  {/* Implementation Notice */}
                  <Alert
                    message='Important Configuration Note'
                    description="Once selected, the business process will determine your facility's operational framework. You can modify this later, but changing it may require staff retraining and workflow adjustments."
                    type='warning'
                    showIcon
                    icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                    style={{
                      borderRadius: '8px',
                      border: `1px solid ${hexToRGBA(token.colorWarning, 0.3)}`
                    }}
                  />
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </Form>
    )
  }
}
